import { useDemoState } from "@/state/demoState";
import { RoomConfig } from "@/state/RoomConfig/RoomConfig";
import { OrbitControls, PerspectiveCamera, OrthographicCamera } from "@react-three/drei";
import React, { useEffect, useMemo, useRef } from "react";
import { Camera } from "three";
import {
    PerspectiveCamera as PerspectiveCameraImpl,
    OrthographicCamera as OrthographicCameraImpl,
} from "three";
import { CameraType } from "./cameraType";
import { usePerspectiveCameraCustomControls } from "./usePerspectiveCameraCustomControls";

type Props = {
    cameraRef: React.MutableRefObject<Camera | null>;
    camera: CameraType;
};

export const CameraManagement: React.FC<Props> = ({ cameraRef, camera }) => {
    const isDragging = useDemoState((state) => state.isDragging);
    const perspectiveCameraRef = useRef<PerspectiveCameraImpl>(null);
    const orthographicCameraRef = useRef<OrthographicCameraImpl>(null);
    const roomConfig = useDemoState((state) => state.roomConfig);

    useEffect(() => {
        if (camera === "perspective" && perspectiveCameraRef.current != null) {
            cameraRef.current = perspectiveCameraRef.current;
        } else if (camera === "orthographic" && orthographicCameraRef.current != null) {
            cameraRef.current = orthographicCameraRef.current;
        }
    }, [camera]);

    const { perspectiveCameraTarget, handlePerspectiveCameraChange } =
        usePerspectiveCameraCustomControls(camera, perspectiveCameraRef);

    const orthoInitialProps = useMemo(() => getStartOrthographicProps(roomConfig), [roomConfig]);

    return (
        <>
            <PerspectiveCamera
                makeDefault={camera === "perspective"}
                position={[-3, 1, 1]}
                ref={perspectiveCameraRef}
            />
            <OrthographicCamera
                makeDefault={camera === "orthographic"}
                position={orthoInitialProps.position}
                zoom={orthoInitialProps.zoom}
                ref={orthographicCameraRef}
            />

            <OrbitControls
                enabled={!isDragging && camera === "perspective"}
                minPolarAngle={Math.PI / 8}
                maxPolarAngle={(Math.PI / 8) * 5}
                target={perspectiveCameraTarget}
                onChange={handlePerspectiveCameraChange}
                camera={perspectiveCameraRef.current ?? undefined}
                enableZoom={true}
                dampingFactor={0.1}
            />

            <OrbitControls
                enabled={!isDragging && camera === "orthographic"}
                enableRotate={false}
                maxAzimuthAngle={0}
                minAzimuthAngle={0}
                maxPolarAngle={0}
                minPolarAngle={0}
                rotation={[-Math.PI / 2, 0, 0]}
                target={[orthoInitialProps.position[0], 0, orthoInitialProps.position[2]]}
                camera={orthographicCameraRef.current ?? undefined}
            />
        </>
    );
};

const getStartOrthographicProps = (roomConfig: RoomConfig) => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const visibleCanvasArea = document.getElementById("VisibleCanvasArea");
    const visibleCanvasLeft = visibleCanvasArea?.getBoundingClientRect().left ?? 0;
    const availableWidth = visibleCanvasArea?.getBoundingClientRect().width ?? windowWidth;

    const desiredWidth = availableWidth * 0.75;
    const desiredHeight = windowHeight * 0.75;

    const desiredWidthZoom = getZoomByPhysicalAndScreenSizes(roomConfig.width, desiredWidth);
    const desiredHeightZoom = getZoomByPhysicalAndScreenSizes(roomConfig.depth, desiredHeight);

    const zoom = Math.min(desiredWidthZoom, desiredHeightZoom);

    const zoomedCenterPositionX = windowWidth / 2 - visibleCanvasLeft - availableWidth / 2;
    const cameraPositionX = getSinzInMByZoom(zoomedCenterPositionX, zoom);

    return {
        zoom,
        position: [cameraPositionX, roomConfig.height + 0.5, 0] as const,
    };
};

const getZoomByPhysicalAndScreenSizes = (sizeInM: number, sizeInPx: number) => sizeInPx / sizeInM;

const getSinzInMByZoom = (sizeInPx: number, zoom: number) => sizeInPx / zoom;
